import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subscription, combineLatest } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthService } from './auth.service';
import { ChatbotOverlayService } from './chatbot-overlay.service';

/**
 * AI Wakeup Service
 * 
 * Keeps the AI chatbot model warm by periodically calling the /wakeup endpoint.
 * 
 * Features:
 * - Immediate wakeup call on login/session initialization
 * - Periodic calls every 5 minutes when model is healthy
 * - Automatic retry every 40 seconds when model is cold/unreachable
 * - Pauses when chatbot is open/active
 * - Resumes with immediate call when chatbot closes
 * - Stops on logout or session expiration
 * - Prevents overlapping requests
 */
@Injectable({
  providedIn: 'root'
})
export class AiWakeupService implements OnDestroy {

  // Timer intervals
  private readonly NORMAL_INTERVAL_MS = 5 * 60 * 1000;  // 5 minutes 5 * 60 * 1000;
  private readonly RETRY_INTERVAL_MS = 30 * 1000;      // 30 seconds

  // State management
  private wakeupTimer: any = null;
  private currentIntervalMs: number = this.NORMAL_INTERVAL_MS;
  private isRequestInFlight = false;
  private chatbotStateSubscription?: Subscription;
  private isServiceActive = false;

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private chatbotOverlay: ChatbotOverlayService
  ) {
    this.initializeChatbotStateMonitoring();
  }

  /**
   * Subscribe to chatbot state changes to pause/resume wakeup service
   */
  private initializeChatbotStateMonitoring(): void {
    this.chatbotStateSubscription = combineLatest([
      this.chatbotOverlay.chatOpen$,
      this.chatbotOverlay.chatMinimized$
    ]).subscribe(([isOpen, isMinimized]) => {
      
      // Stop wakeup only when chatbot is OPEN AND EXPANDED (not minimized)
      if (isOpen && !isMinimized) {
        this.stopService();
      } 
      // Start wakeup when chatbot is CLOSED OR MINIMIZED
      else if (this.isServiceActive) {
        this.wakeup();
      }
    });
  }

  /**
   * Start the wakeup service
   * Triggers immediate wakeup call and schedules periodic calls
   */
  public startWakeupService(): void {
    
    // Guard: don't restart if already active
    if (this.isServiceActive && this.wakeupTimer) {
      return;
    }

    // Guard: only start if user is authopen AND expanded (not minimized)
    if (this.chatbotOverlay.isChatOpen && !this.chatbotOverlay.isChatMinimized) {
      this.isServiceActive = true; // Mark as active so it resumes when chatbot closes/minimize
      return;
      
    }

    this.isServiceActive = true;

    // Clear any existing timer
    this.clearTimer();

    // Reset to normal interval on start
    this.currentIntervalMs = this.NORMAL_INTERVAL_MS;

    // Immediate wakeup call
    this.wakeup();

    // Schedule next call
    // this.scheduleNextWakeup(this.currentIntervalMs);
  }

  /**
   * Stop the wakeup service
   * Clears timers and marks service as inactive
   */
  public stopWakeupService(): void {
    this.isServiceActive = false;
    this.stopService();
  }

  /**
   * Perform the wakeup HTTP call (fire-and-forget)
   */
  private wakeup(): void {
    // Prevent overlapping requests
    if (this.isRequestInFlight) {
      return;
    }

    // Guard: don't make call if service is not  AND expanded (not minimized)
    if (this.chatbotOverlay.isChatOpen && !this.chatbotOverlay.isChatMinimized) {
      return;
    }


    this.isRequestInFlight = true;
    const wakeupUrl = `${environment.chatbotServer}/wakeup`;

    this.http.get(wakeupUrl)
      .pipe()
      .subscribe({
        next: () => {
          this.isRequestInFlight = false;
          this.onWakeupSuccess();
        },
        error: (error) => {
          this.isRequestInFlight = false;
          this.onWakeupError(error);
        }
      });
  }

  /**
   * Handle successful wakeup response
   */
  private onWakeupSuccess(): void {
    this.currentIntervalMs = this.NORMAL_INTERVAL_MS;

    this.scheduleNextWakeup(this.currentIntervalMs);
  }

  /**
   * Handle wakeup error (network failure, timeout, non-2xx response)
   */
  private onWakeupError(error: any): void {
    this.currentIntervalMs = this.RETRY_INTERVAL_MS;

    this.scheduleNextWakeup(this.currentIntervalMs);

  }

  /**
   * Schedule the next wakeup call
   */
  private scheduleNextWakeup(intervalMs: number): void {
    // Guard: don't schedule if service is not active
    if (!this.isServiceActive) {
      return;
    }

    // Clear any existing timer first
    this.clearTimer();

    this.wakeupTimer = setTimeout(() => {
      this.wakeup();
    }, intervalMs);
  }

  /**
   * Clear the current wakeup timer
   */
  private clearTimer(): void {
    if (this.wakeupTimer) {
      clearTimeout(this.wakeupTimer);
      this.wakeupTimer = null;
    }
  }

  /**
   * Stop the service (clear timer, reset state)
   */
  private stopService(): void {
    this.clearTimer();
  }

  /**
   * Cleanup on service destruction
   */
  ngOnDestroy(): void {
    this.isServiceActive = false;
    this.stopService();
    
    if (this.chatbotStateSubscription) {
      this.chatbotStateSubscription.unsubscribe();
    }
  }

}
